import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  CheckCircle,
  Clock,
  FileText,
  Info,
  UploadSimple,
  WarningCircle,
  X,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import Nav from "@/components/Nav";
import WorkingHourglass from "@/components/WorkingHourglass";
import NewCallModeSelector from "@/components/NewCallModeSelector";
import { api } from "@/lib/api";
import { getActionableError } from "@/lib/errors";
import { useAuth } from "@/context/AuthContext";
import { trackProductEvent } from "@/lib/productAnalytics";
import { desktopNotificationState, enableDesktopNotifications } from "@/lib/pushNotifications";

const SUPPORTED_EXTENSIONS = /\.(txt|md|docx|vtt|srt|csv)$/i;

function defaultCallDateTime() {
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
}

function callDateTimeInputValue(value) {
  if (!value) return defaultCallDateTime();
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return defaultCallDateTime();
  const local = new Date(parsed.getTime() - parsed.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
}

function uniqueValues(values) {
  return [...new Set(values.map((value) => String(value || "").trim()).filter(Boolean))];
}

function formatTurnTime(seconds) {
  const numeric = Math.max(0, Number(seconds) || 0);
  const minutes = Math.floor(numeric / 60);
  const remainder = Math.floor(numeric % 60);
  return `${minutes}:${String(remainder).padStart(2, "0")}`;
}

function formatElapsed(seconds) {
  const numeric = Math.max(0, Number(seconds) || 0);
  const minutes = Math.floor(numeric / 60);
  const remainder = Math.floor(numeric % 60);
  return `${minutes}:${String(remainder).padStart(2, "0")}`;
}

export default function TranscriptImportPage() {
  const { legalVersion } = useAuth();
  const location = useLocation();
  const importQuery = new URLSearchParams(location.search);
  const queryCustomer = (importQuery.get("customerName") || "").trim();
  const prefilledRelationshipId = (importQuery.get("relationshipId") || "").trim();
  const prefilledCustomer = typeof location.state?.customerName === "string"
    ? location.state.customerName.trim()
    : queryCustomer;
  const nav = useNavigate();
  const fileInputRef = useRef(null);
  const completionHandledRef = useRef(false);
  const [inputMode, setInputMode] = useState("file");
  const [file, setFile] = useState(null);
  const [pastedText, setPastedText] = useState("");
  const [preview, setPreview] = useState(null);
  const [selectedCandidateId, setSelectedCandidateId] = useState("");
  const [repSpeaker, setRepSpeaker] = useState("");
  const [speakerLabelsConfirmed, setSpeakerLabelsConfirmed] = useState(false);
  const [title, setTitle] = useState("");
  const [customer, setCustomer] = useState(() => prefilledCustomer);
  const [occurredAt, setOccurredAt] = useState(defaultCallDateTime);
  const [authority, setAuthority] = useState(false);
  const [customerSuggestions, setCustomerSuggestions] = useState([]);
  const [titleSuggestions, setTitleSuggestions] = useState([]);
  const [limits, setLimits] = useState(null);
  const [pageError, setPageError] = useState("");
  const [previewing, setPreviewing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [activeCallId, setActiveCallId] = useState("");
  const [processingStatus, setProcessingStatus] = useState("");
  const [processingStartedAt, setProcessingStartedAt] = useState(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [notificationState, setNotificationState] = useState("unknown");
  const [dragActive, setDragActive] = useState(false);

  const candidate = useMemo(
    () => (preview?.candidates || []).find((item) => item.id === selectedCandidateId) || null,
    [preview, selectedCandidateId],
  );
  const quotaExhausted = Boolean(limits?.quota_enforced && limits?.uploaded_calls_remaining === 0);
  const maxImportMb = limits?.max_transcript_import_mb || 5;
  const maxCharacters = limits?.max_transcript_characters || 80000;
  const cleanTitle = title.trim();
  const cleanCustomer = customer.trim();
  const canConfirm = Boolean(
    candidate
    && repSpeaker
    && cleanTitle
    && cleanCustomer
    && occurredAt
    && authority
    && (!candidate.speaker_review_required || speakerLabelsConfirmed)
    && !quotaExhausted
    && !busy,
  );

  useEffect(() => {
    if (!prefilledRelationshipId) return undefined;
    let active = true;
    api.get(`/customers/${encodeURIComponent(prefilledRelationshipId)}/timeline`)
      .then(({ data }) => {
        const canonicalName = String(data?.customer?.name || "").trim();
        if (!active || !canonicalName) return;
        setCustomer((current) => {
          const normalizedCurrent = String(current || "").trim();
          if (!normalizedCurrent || normalizedCurrent === prefilledCustomer) return canonicalName;
          return current;
        });
      })
      .catch(() => {
        // The URL also carries the visible customer name, so a timeline lookup
        // failure must never block or clear the prefill.
      });
    return () => {
      active = false;
    };
  }, [prefilledCustomer, prefilledRelationshipId]);

  useEffect(() => {
    let active = true;
    Promise.allSettled([api.get("/calls"), api.get("/beta/limits")]).then(([callsResult, limitsResult]) => {
      if (!active) return;
      if (callsResult.status === "fulfilled" && Array.isArray(callsResult.value.data)) {
        const calls = callsResult.value.data;
        setTitleSuggestions(uniqueValues(calls.map((call) => call.title)));
        setCustomerSuggestions(uniqueValues(calls.flatMap((call) => [call.customer_name, call.company])));
      }
      if (limitsResult.status === "fulfilled") setLimits(limitsResult.value.data || null);
    });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!busy) {
      setElapsedSeconds(0);
      return undefined;
    }
    const parsedStartedAt = processingStartedAt ? new Date(processingStartedAt).getTime() : Date.now();
    const startedAt = Number.isFinite(parsedStartedAt) ? parsedStartedAt : Date.now();
    const update = () => setElapsedSeconds(Math.max(0, Math.floor((Date.now() - startedAt) / 1000)));
    update();
    const timer = window.setInterval(update, 1000);
    return () => window.clearInterval(timer);
  }, [busy, processingStartedAt]);

  useEffect(() => {
    if (!activeCallId) return undefined;
    let cancelled = false;
    let timer = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/calls/${activeCallId}/processing`);
        if (cancelled || !data) return;
        setProcessingStatus(data.status || "analyzing");
        if (["ready", "partial"].includes(data.status) && !completionHandledRef.current) {
          completionHandledRef.current = true;
          if (data.status === "ready") toast.success("Your imported conversation is ready");
          else toast.warning(data.error || "The transcript is saved. Open the call to retry the dissection.");
          nav(`/calls/${activeCallId}`, { state: { from: "transcript-import" } });
          return;
        }
        if (data.status === "failed" && !completionHandledRef.current) {
          completionHandledRef.current = true;
          setBusy(false);
          setActiveCallId("");
          setProcessingStartedAt(null);
          const message = data.error || "This transcript could not be processed.";
          setPageError(message);
          toast.error(message);
          return;
        }
        timer = window.setTimeout(poll, Number(data.poll_after_ms) || 2000);
      } catch {
        if (!cancelled) timer = window.setTimeout(poll, 3000);
      }
    };
    poll();
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [activeCallId, nav]);

  const dismissStoredPreview = async () => {
    const importId = preview?.id;
    if (!importId) return;
    try {
      await api.delete(`/calls/transcript-import/${importId}`);
    } catch {
      // Preview records also expire automatically; reset must never be blocked.
    }
  };

  const resetPreview = async () => {
    await dismissStoredPreview();
    setPreview(null);
    setSelectedCandidateId("");
    setRepSpeaker("");
    setSpeakerLabelsConfirmed(false);
    setAuthority(false);
    setPageError("");
  };

  const chooseMode = async (nextMode) => {
    if (nextMode === inputMode) return;
    await resetPreview();
    setInputMode(nextMode);
    setFile(null);
    setPastedText("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const enableNotifications = async () => {
    setNotificationState("enabling");
    try {
      const state = await enableDesktopNotifications();
      if (state === "default") {
        setNotificationState("permission-help");
        toast.info("Your browser did not show the notification permission prompt.");
        return;
      }
      setNotificationState(state);
      if (state === "enabled") toast.success("Desktop notification enabled");
      else if (state === "denied") toast.info("Desktop notifications remain off");
    } catch {
      setNotificationState("unavailable");
      toast.warning("Desktop notifications could not be enabled on this browser.");
    }
  };

  const showError = (message) => {
    setPageError(message);
    toast.error(message);
  };

  const pickFile = async (picked) => {
    if (!picked || previewing || busy) return;
    setPageError("");
    if (!SUPPORTED_EXTENSIONS.test(picked.name)) {
      showError("Use a TXT, MD, DOCX, VTT, or Granola CSV transcript.");
      return;
    }
    if (picked.size > maxImportMb * 1024 * 1024) {
      showError(`Transcript files can be up to ${maxImportMb} MB.`);
      return;
    }
    if (preview) await resetPreview();
    setFile(picked);
  };

  const applyCandidate = (nextCandidate) => {
    if (!nextCandidate) return;
    setSelectedCandidateId(nextCandidate.id);
    setTitle(nextCandidate.title || "Imported transcript");
    setOccurredAt(callDateTimeInputValue(nextCandidate.occurred_at));
    // Only semantic labels such as Me/You are safe to suggest. Named speakers
    // require an explicit choice instead of silently selecting the first one.
    setRepSpeaker(nextCandidate.rep_speaker_suggestion || "");
    setSpeakerLabelsConfirmed(false);
    setAuthority(false);
  };

  const createPreview = async () => {
    setPageError("");
    if (quotaExhausted) return showError("Your private-beta call allowance has been used.");
    if (inputMode === "file" && !file) return showError("Choose a transcript file first.");
    if (inputMode === "paste" && !pastedText.trim()) return showError("Paste a speaker-labelled transcript first.");
    if (inputMode === "paste" && pastedText.length > maxCharacters) {
      return showError(`Pasted transcripts can be up to ${maxCharacters.toLocaleString()} characters.`);
    }
    setPreviewing(true);
    const formData = new FormData();
    if (inputMode === "file") formData.append("source_file", file);
    else formData.append("pasted_text", pastedText.trim());
    try {
      const { data } = await api.post("/calls/transcript-import/preview", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      const candidates = Array.isArray(data?.candidates) ? data.candidates : [];
      if (!candidates.length) throw new Error("No meeting candidates returned");
      setPreview(data);
      applyCandidate(candidates[0]);
      trackProductEvent("transcript_import_previewed", {
        section: data?.source_provider === "granola" ? "granola" : data?.source_format || "unknown",
        call_count: candidates.length,
        outcome: "speakers_detected",
      });
      toast.success(candidates.length > 1 ? `${candidates.length} meetings found. Choose one to import.` : "Transcript ready for speaker mapping");
    } catch (error) {
      showError(getActionableError(error, "Callsect could not read this transcript."));
    } finally {
      setPreviewing(false);
    }
  };

  const confirm = async (event) => {
    event.preventDefault();
    setPageError("");
    if (!canConfirm) return showError("Complete the meeting, speaker, customer, and permission checks first.");
    const callDate = new Date(occurredAt);
    if (Number.isNaN(callDate.getTime())) return showError("Choose a valid conversation date and time.");
    setBusy(true);
    setProcessingStatus("queued");
    setProcessingStartedAt(new Date().toISOString());
    setElapsedSeconds(0);
    completionHandledRef.current = false;
    try {
      const { data } = await api.post(`/calls/transcript-import/${preview.id}/confirm`, {
        candidate_id: candidate.id,
        rep_speaker: repSpeaker,
        title: cleanTitle,
        customer_name: cleanCustomer,
        occurred_at: callDate.toISOString(),
        speaker_labels_confirmed: speakerLabelsConfirmed,
        authority_confirmed: true,
        legal_version: legalVersion,
      });
      const callId = data?.processing?.call_id || data?.id;
      if (!callId) throw new Error("Missing call identifier");
      setActiveCallId(callId);
      setProcessingStatus(data?.processing?.status || "queued");
      if (data?.processing?.started_at) setProcessingStartedAt(data.processing.started_at);
      desktopNotificationState().then(setNotificationState).catch(() => setNotificationState("unavailable"));
      trackProductEvent("transcript_import_started", {
        section: preview.source_provider === "granola" ? "granola" : preview.source_format,
        outcome: "analysis_queued",
      });
    } catch (error) {
      setBusy(false);
      setProcessingStatus("");
      setProcessingStartedAt(null);
      showError(getActionableError(error, "This transcript could not be sent to Callsect."));
    }
  };

  return (
    <div className="upload-page min-h-screen">
      <Nav />
      <main className="mx-auto w-full max-w-6xl px-4 py-8 sm:px-6 sm:py-10 lg:px-8 lg:py-14 product-page-enter">
                <header className="upload-hero mb-8 sm:mb-10">
          <div>
            <div className="brand-eyebrow">NEW CALL</div>
            <h1 className="mt-4 text-4xl font-semibold tracking-[-0.045em] text-white sm:text-5xl">
              Add a new call
            </h1>
            <p className="mt-4 max-w-2xl text-sm leading-7 text-slate-400 sm:text-base">
              Add a recording or transcript, then confirm the conversation details.
            </p>
          </div>
          <div className="upload-hero__flow" aria-label="Upload flow">
            <span className={!preview ? "is-active" : ""}><strong>1</strong> Add conversation</span>
            <span className={preview && !busy ? "is-active" : ""}><strong>2</strong> Confirm context</span>
            <span className={busy ? "is-active" : ""}><strong>3</strong> Review results</span>
          </div>
        </header>

        <NewCallModeSelector
          activeMode="transcript"
          relationshipId={prefilledRelationshipId}
          customerName={cleanCustomer || prefilledCustomer}
          from={location.state?.from || "new-call"}
        />

        {pageError && (
          <div className="upload-error-banner mb-6" role="alert">
            <WarningCircle size={20} weight="duotone" />
            <div><strong>This transcript needs attention</strong><span>{pageError}</span></div>
            <button type="button" onClick={() => setPageError("")} aria-label="Dismiss transcript error"><X size={16} weight="bold" /></button>
          </div>
        )}

        <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_20rem]">
          <form onSubmit={confirm} className="surface-card overflow-visible p-4 sm:p-6 lg:p-7">
            {!preview ? (
              <>
                <div className="transcript-import-source-tabs grid grid-cols-2 gap-2 rounded-2xl border border-white/[0.08] bg-black/20 p-1.5" role="tablist" aria-label="Transcript source">
                  <button type="button" onClick={() => chooseMode("file")} className={`transcript-import-source-tab rounded-xl px-4 py-3 text-sm font-semibold transition ${inputMode === "file" ? "is-active bg-violet-600 text-white" : "text-slate-400 hover:bg-white/[0.04] hover:text-white"}`}>
                    <span className="flex items-center justify-center gap-2"><UploadSimple size={17} /> Upload file</span>
                  </button>
                  <button type="button" onClick={() => chooseMode("paste")} className={`transcript-import-source-tab rounded-xl px-4 py-3 text-sm font-semibold transition ${inputMode === "paste" ? "is-active bg-violet-600 text-white" : "text-slate-400 hover:bg-white/[0.04] hover:text-white"}`}>
                    <span className="flex items-center justify-center gap-2"><FileText size={17} /> Paste text</span>
                  </button>
                </div>

                {inputMode === "file" ? (
                  <div
                    className={`upload-dropzone mt-5 ${dragActive ? "is-dragging" : ""} ${file ? "has-file" : ""}`}
                    onClick={() => fileInputRef.current?.click()}
                    onKeyDown={(event) => {
                      if (["Enter", " "].includes(event.key)) {
                        event.preventDefault();
                        fileInputRef.current?.click();
                      }
                    }}
                    onDragEnter={(event) => { event.preventDefault(); setDragActive(true); }}
                    onDragOver={(event) => { event.preventDefault(); setDragActive(true); }}
                    onDragLeave={(event) => { event.preventDefault(); if (!event.currentTarget.contains(event.relatedTarget)) setDragActive(false); }}
                    onDrop={(event) => { event.preventDefault(); setDragActive(false); pickFile(event.dataTransfer.files?.[0]); }}
                    role="button"
                    tabIndex={0}
                  >
                    <input ref={fileInputRef} type="file" accept=".txt,.md,.docx,.vtt,.srt,.csv" className="hidden" onChange={(event) => pickFile(event.target.files?.[0])} />
                    {file ? (
                      <div className="relative z-10 flex w-full items-center gap-4 text-left">
                        <span className="rounded-xl border border-violet-400/25 bg-violet-500/10 p-3 text-violet-200"><FileText size={26} weight="duotone" /></span>
                        <div className="min-w-0 flex-1"><div className="truncate font-medium text-white">{file.name}</div><div className="mt-1 text-xs text-emerald-300">{(file.size / 1024).toFixed(1)} KB · ready to preview</div></div>
                        <button type="button" className="upload-clear-button" onClick={(event) => { event.stopPropagation(); setFile(null); if (fileInputRef.current) fileInputRef.current.value = ""; }} aria-label="Remove transcript file"><X size={18} /></button>
                      </div>
                    ) : (
                      <div className="relative z-10 flex flex-col items-center">
                        <div className="upload-icon-shell"><UploadSimple size={30} weight="light" /></div>
                        <div className="mt-5 text-base font-medium text-white sm:text-lg">Drop a full transcript here</div>
                        <div className="mt-2 text-xs uppercase tracking-[0.18em] text-slate-500">txt · md · docx · vtt · srt · granola csv · max {maxImportMb}mb</div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="mt-5">
                    <label className="upload-field-label" htmlFor="pasted-transcript">Full speaker-labelled transcript</label>
                    <textarea
                      id="pasted-transcript"
                      value={pastedText}
                      onChange={(event) => setPastedText(event.target.value)}
                      className="transcript-import-textarea mt-2 min-h-[18rem] w-full rounded-2xl border border-white/[0.1] bg-black/20 p-4 text-sm leading-6 text-slate-200 outline-none transition placeholder:text-slate-600 focus:border-violet-400/50 focus:ring-2 focus:ring-violet-500/10"
                      placeholder={"Me: Thanks for making time. I wanted to review the renewal.\n\nThem: Happy to. Budget is tight and we need to see more adoption.\n\nMe: Which outcome would make the decision easier?"}
                      maxLength={maxCharacters}
                    />
                    <div className="mt-2 flex justify-end text-xs text-slate-600"><span>{pastedText.length.toLocaleString()}/{maxCharacters.toLocaleString()}</span></div>
                  </div>
                )}

                <button type="button" onClick={createPreview} disabled={previewing || quotaExhausted || (inputMode === "file" ? !file : !pastedText.trim())} className="upload-submit-button upload-submit-button--compact-gate mt-6">
                  {previewing && <WorkingHourglass size={15} />}
                  {previewing ? "Reading speakers…" : "Review speakers"}
                </button>
                <p className="upload-submit-note">Nothing is analyzed yet. You’ll review the detected speakers first.</p>
              </>
            ) : (
              <>
                <div className={`flex flex-wrap items-start justify-between gap-4 rounded-2xl border p-4 ${candidate?.speaker_review_required ? "border-amber-400/25 bg-amber-400/[0.055]" : "border-emerald-400/20 bg-emerald-400/[0.055]"}`}>
                  <div className="flex items-start gap-3">{candidate?.speaker_review_required ? <WarningCircle size={22} weight="fill" className="mt-0.5 text-amber-300" /> : <CheckCircle size={22} weight="fill" className="mt-0.5 text-emerald-300" />}<div><strong className="text-sm text-white">{candidate?.speaker_review_required ? "Transcript interpreted — speaker review required" : (preview.source_provider === "granola" ? "Granola transcript detected" : "Transcript structure verified")}</strong><p className="mt-1 text-xs leading-5 text-slate-400">{preview.source_filename} · {preview.candidates.length} meeting{preview.candidates.length === 1 ? "" : "s"} available</p></div></div>
                  <button type="button" onClick={resetPreview} disabled={busy} className="inline-flex items-center gap-2 rounded-xl border border-white/[0.1] px-3 py-2 text-xs font-semibold text-slate-300 hover:border-white/20 hover:text-white"><ArrowLeft size={14} /> Replace source</button>
                </div>

                {preview.candidates.length > 1 && (
                  <div className="mt-6">
                    <label className="upload-field-label" htmlFor="meeting-candidate">Meeting to import</label>
                    <select id="meeting-candidate" className="upload-input mt-2" value={selectedCandidateId} onChange={(event) => applyCandidate(preview.candidates.find((item) => item.id === event.target.value))} disabled={busy}>
                      {preview.candidates.map((item, index) => <option key={item.id} value={item.id}>{index + 1}. {item.title} · {item.turn_count} turns</option>)}
                    </select>
                    <p className="mt-2 text-xs text-slate-600">One meeting is imported at a time. Re-upload the CSV to select another meeting later.</p>
                  </div>
                )}

                {candidate && (
                  <>
                    <section className="transcript-speaker-map mt-6 rounded-2xl border border-white/[0.08] bg-black/15 p-4 sm:p-5" aria-labelledby="speaker-map-title">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div><div className="brand-eyebrow">Speaker mapping</div><h2 id="speaker-map-title" className="mt-2 text-xl font-semibold text-white">Which speaker is you?</h2><p className="mt-2 text-xs leading-5 text-slate-500">The selected speaker becomes YOU. Every other detected speaker becomes CLIENT.</p></div>
                        <span className="transcript-speaker-map__summary rounded-full border border-white/[0.08] px-3 py-1.5 text-xs text-slate-400">{candidate.speakers.length} speakers · {candidate.turn_count} turns</span>
                      </div>
                      <div className="mt-4 grid gap-2 sm:grid-cols-2">
                        {candidate.speakers.map((speaker) => {
                          const selected = repSpeaker === speaker;
                          return (
                            <button
                              key={speaker}
                              type="button"
                              onClick={() => { setRepSpeaker(speaker); setSpeakerLabelsConfirmed(false); }}
                              disabled={busy}
                              className={`transcript-speaker-map__option flex items-center justify-between rounded-xl border p-3 text-left transition ${selected ? "is-selected border-violet-400/50 bg-violet-500/10 text-white" : "is-unselected border-white/[0.08] bg-white/[0.02] text-slate-400 hover:border-white/20"}`}
                            >
                              <span className="truncate text-sm font-medium">{speaker}</span>
                              <span className={`transcript-speaker-map__role ml-3 rounded-full px-2.5 py-1 text-[11px] font-bold ${selected ? "is-selected bg-violet-500 text-white" : "is-unselected bg-white/[0.05] text-slate-500"}`}>{selected ? "YOU" : (repSpeaker ? "CLIENT" : "SELECT")}</span>
                            </button>
                          );
                        })}
                      </div>
                      {candidate.speaker_review_required && (
                        <div className="transcript-speaker-map__review mt-4 rounded-xl border border-amber-400/20 bg-amber-400/[0.05] p-3">
                          <label className="transcript-speaker-map__checkbox flex items-start gap-3 text-xs leading-5 text-slate-200">
                            <input type="checkbox" checked={speakerLabelsConfirmed} onChange={(event) => setSpeakerLabelsConfirmed(event.target.checked)} disabled={busy || !repSpeaker} className="mt-0.5 h-4 w-4 shrink-0 accent-violet-500" />
                            <span>I’ve reviewed the speakers and confirmed that everyone other than YOU is on the customer side.</span>
                          </label>
                        </div>
                      )}
                    </section>

                    <section className="transcript-import-preview mt-5 overflow-hidden rounded-2xl border border-white/[0.08] bg-[#090912]" aria-labelledby="transcript-preview-title">
                      <div className="transcript-import-preview__header flex flex-wrap items-center justify-between gap-3 border-b border-white/[0.07] px-4 py-3"><div><strong id="transcript-preview-title" className="text-sm text-white">Transcript preview</strong><span className="ml-2 text-xs text-slate-600">first {candidate.preview_segments.length} of {candidate.turn_count} turns</span></div><span className="text-xs text-slate-500">{candidate.timestamps_exact ? "Source timestamps" : "Positions estimated from turn length"}</span></div>
                      <div className="max-h-[22rem] space-y-1 overflow-y-auto p-3 intel-scrollbar">
                        {candidate.preview_segments.map((segment, index) => {
                          const isYou = segment.speaker === repSpeaker;
                          const displayedRole = repSpeaker ? (isYou ? "YOU" : "CLIENT") : segment.speaker;
                          return (
                            <div key={`${segment.speaker}-${index}`} className="transcript-import-preview__row grid grid-cols-[3.25rem_5rem_minmax(0,1fr)] gap-2 rounded-xl border border-white/[0.045] bg-white/[0.018] px-3 py-2.5 text-xs">
                              <span className="font-mono text-slate-600">{segment.timestamp_supplied ? formatTurnTime(segment.start) : `#${index + 1}`}</span>
                              <span title={displayedRole} className={`truncate ${isYou ? "font-bold text-violet-300" : "font-bold text-cyan-300"}`}>{displayedRole}</span>
                              <span className="leading-5 text-slate-300">{segment.text}</span>
                            </div>
                          );
                        })}
                      </div>
                    </section>

                    <div className="my-6 segmented-divider" />
                    <fieldset disabled={busy} className="space-y-5 disabled:opacity-65">
                      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                        <ImportField label="Call title" value={title} setValue={setTitle} placeholder="Renewal call — Q3" suggestions={titleSuggestions} />
                        <ImportField label="Customer" value={customer} setValue={setCustomer} placeholder="John Griffin or Acme Corp" suggestions={customerSuggestions} />
                      </div>
                      <div><label className="upload-field-label" htmlFor="transcript-occurred-at">Conversation date and time</label><input id="transcript-occurred-at" type="datetime-local" className="upload-input mt-2" value={occurredAt} onChange={(event) => setOccurredAt(event.target.value)} /></div>
                      <label className="upload-consent">
                        <input type="checkbox" checked={authority} onChange={(event) => setAuthority(event.target.checked)} className="mt-1 h-4 w-4 shrink-0 accent-violet-500" />
                        <span>I confirm that I am authorised to upload and process this transcript and have provided any notice or obtained any consent required by applicable law. See the <Link to="/privacy" target="_blank" className="text-violet-300 hover:text-violet-200">Privacy Notice</Link> and <Link to="/data-handling" target="_blank" className="text-violet-300 hover:text-violet-200">data-handling summary</Link>.</span>
                      </label>
                    </fieldset>

                    <button type="submit" disabled={!canConfirm} className="upload-submit-button upload-submit-button--compact-gate mt-6">
                      {busy ? <WorkingHourglass size={15} /> : <Check size={15} weight="bold" />}
                      {busy ? "Dissecting transcript…" : "Confirm roles and dissect"}
                    </button>
                    {busy && (
                      <TranscriptProcessingPanel
                        status={processingStatus}
                        elapsedSeconds={elapsedSeconds}
                        notificationState={notificationState}
                        onEnableNotifications={enableNotifications}
                      />
                    )}
                  </>
                )}
              </>
            )}
          </form>

          <aside className="surface-card transcript-import-sidebar overflow-hidden lg:sticky lg:top-24">
            <div className="p-5 sm:p-6">
              <div className="brand-eyebrow">Before you start</div>
              <h2 className="mt-3 text-2xl font-semibold tracking-tight text-white">Upload a complete transcript.</h2>
            </div>
            <div className="segmented-divider" />
            <div className="transcript-import-sidebar__info p-5 text-xs leading-5 text-slate-500"><div className="flex items-start gap-2"><Info size={17} className="transcript-import-sidebar__info-icon mt-0.5 shrink-0 text-cyan-300" /><span>When source timestamps are absent, Callsect estimates positions from turn length and marks that limitation in the saved call metadata.</span></div></div>
            <div className="segmented-divider" />
            <div className="p-5">
              <Link to="/integrations" className="inline-flex items-center gap-2 text-sm font-semibold text-violet-300 hover:text-violet-200">Connect Google Meet <ArrowRight size={16} /></Link>
            </div>
          </aside>
        </div>
      </main>
    </div>
  );
}

function TranscriptProcessingPanel({ status, elapsedSeconds, notificationState, onEnableNotifications }) {
  const queued = status === "queued";

  return (
    <section className="processing-panel mt-5" role="status" aria-live="polite" data-testid="dissection-progress">
      <div className="processing-panel__visual" aria-hidden="true">
        <div className="dissection-lines is-dissecting"><span /><span /><span /><span /><span /></div>
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <div className="processing-panel__title text-sm font-semibold text-white">
              <WorkingHourglass size={15} />
              <span>{queued ? "Preparing Dissection..." : "Dissecting conversation..."}</span>
            </div>
            <div className="mt-1 text-xs leading-5 text-slate-500">
              This can take up to a minute. You can safely leave Callsect while it finishes.
            </div>
          </div>
          <div className="processing-panel__meta">
            <span>Stage 1 of 1</span>
            <span><Clock size={13} /> {formatElapsed(elapsedSeconds)}</span>
          </div>
        </div>

        <div className="mt-4 space-y-3">
          <div className="processing-step is-active">
            <div className="processing-step__marker">1</div>
            <div className="min-w-0">
              <div className="text-sm font-medium">Dissecting conversation</div>
              <div className="mt-0.5 text-xs text-slate-600">Connecting signals, context, coaching, and relationship memory.</div>
            </div>
          </div>
        </div>

        {["default", "enabling"].includes(notificationState) && (
          <div className="processing-panel__notification">
            <div>
              <strong>Want a desktop alert when this call is ready?</strong>
              <span>You can close Callsect now. Processing will continue in the background.</span>
            </div>
            <button type="button" onClick={onEnableNotifications} disabled={notificationState === "enabling"}>
              {notificationState === "enabling" ? "Enabling..." : "Enable notifications"}
            </button>
          </div>
        )}

        {notificationState === "permission-help" && (
          <div className="processing-panel__notification">
            <div>
              <strong>Your browser kept notifications on Ask.</strong>
              <span>Use the site controls beside the address bar to allow notifications for callsect.com, then retry.</span>
            </div>
            <button type="button" onClick={onEnableNotifications}>Retry</button>
          </div>
        )}

        {notificationState === "enabled" && (
          <div className="processing-panel__notification is-enabled">
            <CheckCircle size={16} weight="fill" />
            <span>Desktop alert enabled. You can leave Callsect and we will let you know when this is ready.</span>
          </div>
        )}

        {elapsedSeconds >= 90 && (
          <div className="processing-panel__patience-note">
            <Info size={15} weight="duotone" />
            Longer calls can take more time. Processing is still active; do not import the same transcript again.
          </div>
        )}
      </div>
    </section>
  );
}

function ImportField({ label, value, setValue, placeholder, suggestions }) {
  const listId = `transcript-${label.toLowerCase().replace(/[^a-z]+/g, "-")}-suggestions`;
  return (
    <div><label className="upload-field-label" htmlFor={listId.replace("-suggestions", "")}>{label}</label><input id={listId.replace("-suggestions", "")} className="upload-input mt-2" value={value} onChange={(event) => setValue(event.target.value)} placeholder={placeholder} list={listId} /><datalist id={listId}>{suggestions.map((item) => <option key={item} value={item} />)}</datalist></div>
  );
}

